// Per-agent model preference, persisted per member (localStorage). One model per agent
// (Claude Code / GitHub Copilot), reused across the agentic operations (chat + improve).

const key = (agent: string): string => `niobe.model.${agent}`;
const AGENT_KEY = 'niobe.agent';

export function getModel(agent: string): string | null {
  try {
    return localStorage.getItem(key(agent));
  } catch {
    return null;
  }
}

export function setModel(agent: string, model: string): void {
  try {
    localStorage.setItem(key(agent), model);
  } catch {
    /* storage unavailable — ignore */
  }
}

// "Auto" in the Claude Code model picker (AB#411): no model is sent and the passerelle routes the
// run (mechanical edits on a lighter model, everything else on Opus 5.5). It is saved like any
// other pick, but it is a web-side sentinel, not a catalogue model: it must never reach a runner.
export const AUTO_MODEL = 'auto';

/** The saved model to send to a runner — the Auto sentinel (or nothing saved) reads as no model. */
export function getRunnableModel(agent: string): string | undefined {
  const m = getModel(agent);
  return m && m !== AUTO_MODEL ? m : undefined;
}

// One-time migration (AB#411): before Auto existed, any pick (even the default Opus) was saved and
// switched routing off for good. A saved default Opus becomes Auto once; the flag makes a later
// explicit Opus pick stick. Other saved models (sonnet, haiku, fable) are real choices and stay.
const AUTO_MIGRATION_KEY = 'niobe.model.autoMigrated';
const MIGRATED_TO_AUTO = new Set(['opus', 'claude-opus-5-5']);

export function migrateClaudeModelToAuto(): void {
  try {
    if (localStorage.getItem(AUTO_MIGRATION_KEY)) return;
    const saved = localStorage.getItem(key('Claude Code'));
    if (saved && MIGRATED_TO_AUTO.has(saved)) localStorage.setItem(key('Claude Code'), AUTO_MODEL);
    localStorage.setItem(AUTO_MIGRATION_KEY, '1');
  } catch {
    /* storage unavailable — ignore */
  }
}

export function getAgent(): string | null {
  try {
    return localStorage.getItem(AGENT_KEY);
  } catch {
    return null;
  }
}

export function setAgent(agent: string): void {
  try {
    localStorage.setItem(AGENT_KEY, agent);
  } catch {
    /* storage unavailable — ignore */
  }
}

// Sticky "Enable local skills" preference (Claude Code only, set from the ⓘ agent-info modal).
// Niobe allows local skills by default, so an unset/unavailable value reads as true.
const LOCAL_SKILLS_KEY = 'niobe.localSkills';

export function getLocalSkillsEnabled(): boolean {
  try {
    const v = localStorage.getItem(LOCAL_SKILLS_KEY);
    return v === null ? true : v === 'true';
  } catch {
    return true;
  }
}

export function setLocalSkillsEnabled(v: boolean): void {
  try {
    localStorage.setItem(LOCAL_SKILLS_KEY, String(v));
  } catch {
    /* storage unavailable — ignore */
  }
}

// Per-skill opt-out, layered under the master toggle above: which individually-discovered local
// skills (by name, from GET /agent/local-skills) the member has switched off in the per-skill
// list. Stored as the disabled set (not the enabled one) so a newly-appearing skill on disk
// reads as enabled by default, same as the master toggle's default.
const DISABLED_SKILLS_KEY = 'niobe.disabledLocalSkills';

export function getDisabledLocalSkills(): string[] {
  try {
    const raw = localStorage.getItem(DISABLED_SKILLS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((n): n is string => typeof n === 'string') : [];
  } catch {
    return [];
  }
}

export function setDisabledLocalSkills(names: string[]): void {
  try {
    localStorage.setItem(DISABLED_SKILLS_KEY, JSON.stringify(names));
  } catch {
    /* storage unavailable — ignore */
  }
}

// "Your Claude sign-in expires in N days", dismissed in the chat composer. Stored as the epoch-ms
// instant the dismissal runs out (24 h), not as a boolean: a warning that came back after a day is
// the point, and a boolean would need a second key to say when it was set.
//
// Only the CHAT strip honours this. Settings keeps showing the warning — that page is where the
// member goes to deal with it, so hiding it there would hide the fix along with the problem.
const EXPIRY_DISMISSED_KEY = 'niobe.claudeExpiryDismissedUntil';

export function getClaudeExpiryDismissedUntil(): number {
  try {
    const raw = localStorage.getItem(EXPIRY_DISMISSED_KEY);
    const n = raw === null ? NaN : Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export function setClaudeExpiryDismissedUntil(until: number): void {
  try {
    localStorage.setItem(EXPIRY_DISMISSED_KEY, String(until));
  } catch {
    /* storage unavailable — ignore */
  }
}
